import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutGrid, Table } from 'lucide-react';
import { api } from '../../lib/api';
import { projectStatusMeta } from '../../lib/projects';
import EmptyState from '../../components/crm/EmptyState';
import ProjectsKanban from '../../components/admin/ProjectsKanban';

const VIEW_PREF_KEY = 'nexsav.projects.view';

// Type filter — keeps the service lines (maintenance, media packages)
// visually separate from the build pipeline. 'active' (default) hides them;
// their own chips show only them; 'all' shows everything. Stored in URL
// ?type= so back-button + share-links + page-refresh preserve the view.
const SERVICE_LINE_TYPES = ['maintenance', 'media_package'];
const TYPE_FILTERS = [
  { value: 'active',        label: 'Active build' },
  { value: 'maintenance',   label: 'Maintenance' },
  { value: 'media_package', label: 'Media packages' },
  { value: 'all',           label: 'All' },
];

export default function Projects() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeFilter = searchParams.get('type') || 'active';
  const setTypeFilter = (next) => {
    const sp = new URLSearchParams(searchParams);
    if (next === 'active') sp.delete('type');
    else sp.set('type', next);
    setSearchParams(sp, { replace: false });
  };
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState(() => {
    if (typeof window === 'undefined') return 'kanban';
    return window.localStorage?.getItem(VIEW_PREF_KEY) || 'kanban';
  });
  const [capacity, setCapacity] = useState(null);

  const visibleProjects = useMemo(() => {
    if (typeFilter === 'all') return projects;
    if (typeFilter === 'maintenance') return projects.filter((p) => p.type === 'maintenance');
    if (typeFilter === 'media_package') return projects.filter((p) => p.type === 'media_package');
    return projects.filter((p) => !SERVICE_LINE_TYPES.includes(p.type));
  }, [projects, typeFilter]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/api/admin/projects?limit=500');
      setProjects(res.projects || []);
    } catch (err) {
      setError(err.message || 'Failed to load projects');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Capacity widget (item 2.9). Live metrics computed from project_status_history.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/admin/projects/capacity');
        if (!cancelled) setCapacity(res);
      } catch (err) {
        // Capacity endpoint is best-effort; missing/failing → render without.
        console.warn('capacity load failed', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onProjectChanged = (updated) => {
    setProjects((prev) => prev.map((p) => p.id === updated.id ? { ...p, ...updated } : p));
  };

  const setViewPref = (v) => {
    setView(v);
    try { window.localStorage?.setItem(VIEW_PREF_KEY, v); } catch {}
  };

  return (
    <div className="max-w-[1800px] mx-auto">
      <div className="mb-4 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Projects</span></h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Active project pipeline by status. Live projects roll off into Maintained.</p>
          {capacity && (
            <div className="text-[11px] text-gray-400 mt-2 flex flex-wrap items-center gap-3" data-testid="projects-capacity">
              <span><strong className="text-white">{capacity.inBuildCount ?? 0}</strong> in build</span>
              <span>·</span>
              <span><strong className="text-white">{capacity.inPreviewReviewCount ?? 0}</strong> in preview review</span>
              <span>·</span>
              <span>Avg cycle <strong className="text-white">{capacity.avgBuildCycleDays != null ? `${capacity.avgBuildCycleDays}d` : '—'}</strong></span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] p-0.5" data-testid="projects-type-filter">
            {TYPE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setTypeFilter(f.value)}
                data-testid={`type-filter-${f.value}`}
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] ${typeFilter === f.value ? 'bg-white/[0.08] text-white' : 'text-gray-400 hover:text-white'}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 rounded-full border border-white/[0.08] p-0.5" data-testid="projects-view-toggle">
            <button
              type="button"
              onClick={() => setViewPref('kanban')}
              data-testid="view-kanban"
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] ${view === 'kanban' ? 'bg-white/[0.08] text-white' : 'text-gray-400 hover:text-white'}`}
            >
              <LayoutGrid className="w-3 h-3" /> Kanban
            </button>
            <button
              type="button"
              onClick={() => setViewPref('table')}
              data-testid="view-table"
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] ${view === 'table' ? 'bg-white/[0.08] text-white' : 'text-gray-400 hover:text-white'}`}
            >
              <Table className="w-3 h-3" /> Table
            </button>
          </div>
        </div>
      </div>

      {error && <div className="text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-3">{error}</div>}

      {!loading && visibleProjects.length === 0 ? (
        <EmptyState
          title={typeFilter === 'maintenance' ? 'No maintenance projects yet' : 'No projects yet'}
          description={typeFilter === 'maintenance'
            ? 'Maintenance projects are created from an AccountDetail page using the Maintenance type in New Project.'
            : 'Auto-created when an account transitions to contracted. Create one manually from an AccountDetail page.'}
        />
      ) : view === 'kanban' ? (
        <ProjectsKanban projects={visibleProjects} onChanged={onProjectChanged} />
      ) : (
        <ProjectsTable projects={visibleProjects} onOpen={(p) => navigate(`/admin/projects/${p.id}`)} />
      )}
    </div>
  );
}

function ProjectsTable({ projects, onOpen }) {
  return (
    <div className="rounded-xl border border-white/[0.05] bg-white/[0.01] overflow-hidden" data-testid="projects-table">
      <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-white/[0.02] border-b border-white/[0.05] text-[10px] uppercase tracking-wider text-gray-500">
        <div className="col-span-7 lg:col-span-5">Name</div>
        <div className="hidden lg:block lg:col-span-2">Type</div>
        <div className="col-span-5 lg:col-span-2">Status</div>
        <div className="hidden lg:block lg:col-span-3">Target delivery</div>
      </div>
      {projects.map((p) => {
        const meta = projectStatusMeta(p.status);
        return (
          <button key={p.id} onClick={() => onOpen?.(p)}
            className="w-full grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/[0.03] hover:bg-white/[0.02] text-left">
            <div className="col-span-7 lg:col-span-5 text-[13px] text-white">{p.name}</div>
            <div className="hidden lg:block lg:col-span-2 text-[12px] text-gray-400 uppercase tracking-wider">{p.type}</div>
            <div className="col-span-5 lg:col-span-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${meta.tone}`}>
                {meta.label}
              </span>
            </div>
            <div className="hidden lg:block lg:col-span-3 text-[12px] text-gray-500">{p.targetDeliveryDate || '—'}</div>
          </button>
        );
      })}
    </div>
  );
}
